import { useId, type ReactNode } from "react";

/** Full-viewport sky illustration width in SVG units. */
export const VIEW_WIDTH = 390;

/** Full-viewport sky illustration height in SVG units. */
export const VIEW_HEIGHT = 780;

/** Y coordinate where the garden ground strip begins. */
export const GROUND_Y = VIEW_HEIGHT - 33;

/** Only a handful of stars peek through cloud gaps. */
export const STARS: readonly (readonly [number, number, number, number])[] = [
  [34, 14, 0.9, 0.55],
  [78, 22, 0.6, 0.38],
  [148, 10, 1.1, 0.62],
  [192, 19, 0.7, 0.42],
  [352, 18, 1.0, 0.58],
  [381, 9, 0.8, 0.45],
  [56, 38, 0.6, 0.32],
  [138, 32, 0.8, 0.4],
  [318, 27, 0.7, 0.35],
  [370, 35, 0.5, 0.3],
];

/** Brighter star field for clear night skies. */
export const CLEAR_NIGHT_STARS: readonly (readonly [number, number, number, number])[] = [
  [28, 18, 1.0, 0.7],
  [62, 42, 0.7, 0.5],
  [95, 12, 1.2, 0.75],
  [130, 55, 0.8, 0.45],
  [168, 24, 1.1, 0.65],
  [210, 38, 0.9, 0.55],
  [248, 16, 1.3, 0.8],
  [285, 48, 0.7, 0.42],
  [320, 22, 1.0, 0.6],
  [355, 40, 0.8, 0.48],
  [12, 68, 0.6, 0.35],
  [175, 72, 0.9, 0.4],
  [340, 65, 0.7, 0.38],
];

/** Rain streak positions for rainy sky variants. */
export const RAIN_STREAKS: readonly (readonly [number, number, number])[] = [
  [22, 165, 14], [44, 172, 11], [68, 168, 16], [90, 175, 12],
  [115, 162, 18], [138, 170, 13], [160, 178, 15], [182, 165, 11],
  [205, 172, 17], [228, 168, 12], [252, 175, 14], [275, 162, 16],
  [298, 170, 10], [320, 178, 15], [342, 164, 13], [365, 171, 17],
  [385, 168, 11], [10, 175, 9], [55, 180, 12], [105, 183, 10],
  [148, 185, 14], [195, 180, 11], [240, 182, 13], [285, 185, 9],
  [330, 178, 15], [375, 183, 10],
];

/** Frost crystal positions for frost sky variant. */
export const FROST_CRYSTALS: readonly (readonly [number, number])[] = [
  [60, 70], [120, 55], [80, 110], [180, 40], [230, 90],
  [290, 60], [310, 130], [150, 140], [200, 135], [340, 85],
  [45, 155], [260, 145], [370, 120],
];

/**
 * Returns a sanitized unique id prefix for SVG gradient/pattern defs.
 */
export function useSkyGradientIds(prefix: string): string {
  return `${useId().replace(/:/g, "")}-${prefix}`;
}

/**
 * Full-viewport SVG shell shared by all garden sky illustrations.
 */
export function SkySvgShell({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <svg
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMax slice"
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/**
 * Garden silhouette ground strip anchored to the bottom edge of the viewport.
 */
export function GardenGround({ fill = "#030710" }: Readonly<{ fill?: string }>) {
  return (
    <>
      <rect x="0" y={GROUND_Y} width={VIEW_WIDTH} height="33" fill={fill} />
      <g fill={fill} transform={`translate(0, ${GROUND_Y - 297})`}>
        <path d="M2,297 Q0,278 3,258 Q5,278 8,297Z" />
        <path d="M10,297 Q9,280 13,262 Q15,282 18,297Z" />
        <path d="M19,297 Q18,276 23,258 Q26,278 28,297Z" />
        <path d="M40,297 C32,272 34,250 42,238 C50,250 52,272 46,297Z" />
        <path d="M52,297 C46,275 48,257 54,245 C60,257 60,276 57,297Z" />
        <path d="M29,297 C23,275 26,258 32,248 C38,260 38,276 35,297Z" />
        <path d="M27,293 L60,293 L57,302 L30,302Z" />
        <path d="M100,297 Q99,282 102,268 Q104,282 106,297Z" />
        <path d="M108,297 Q108,286 111,274 Q113,286 114,297Z" />
        <path d="M187,297 C185,276 187,256 187,242 C189,256 191,278 191,297Z" />
        <path d="M177,297 C175,278 177,262 180,250 C183,262 183,280 182,297Z" />
        <path d="M196,297 C196,280 198,265 201,254 C203,266 205,280 203,297Z" />
        <path d="M187,266 C179,263 174,255 178,247 C183,252 187,266 187,266Z" />
        <path d="M187,266 C195,263 200,255 196,247 C191,252 187,266 187,266Z" />
        <circle cx="181" cy="277" r="5.5" />
        <circle cx="193" cy="282" r="5" />
        <circle cx="200" cy="273" r="4.5" />
        <path d="M240,297 Q239,282 242,268 Q244,282 245,297Z" />
        <path d="M249,297 Q249,286 252,274 Q254,286 255,297Z" />
        <path d="M289,297 C283,273 285,250 291,240 C297,250 299,273 295,297Z" />
        <path d="M278,297 C274,276 276,258 281,247 C286,259 286,276 284,297Z" />
        <path d="M300,297 C299,276 302,260 305,250 C309,262 308,278 307,297Z" />
        <path d="M291,266 C283,263 279,255 283,248 C288,252 291,266 291,266Z" />
        <path d="M291,266 C299,263 304,255 300,248 C295,252 291,266 291,266Z" />
        <path d="M350,297 Q349,279 352,263 Q354,279 356,297Z" />
        <path d="M360,297 Q360,281 363,266 Q365,281 367,297Z" />
        <path d="M370,297 Q370,278 374,262 Q376,280 378,297Z" />
        <path d="M380,297 Q381,282 385,268 Q387,282 390,297L390,297Z" />
      </g>
    </>
  );
}
